import { useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from "react";
import {
    HubConnectionState,
    type HubConnection,
} from "@microsoft/signalr";
import type { PublicClientApplication } from "@azure/msal-browser";
import type { AppConfig } from "../auth/authConfig";
import {
    acquireAccessToken,
    createRealtimeConnection,
    isSignInNeeded,
    reconnectDelayMs,
} from "./connection";
import { createGapReporter, createGapTracker, type GapReport } from "./gapTelemetry";
import {
    RealtimeConnectionContext,
    RealtimeStateStoreContext,
    createRealtimeStateStore,
    type RealtimeConnectionState,
} from "./realtimeContext";

// ---------------------------------------------------------------------------
// Provider responsibilities
//
//   1. Spin up exactly one `HubConnection` for the lifetime of the
//      authenticated session and tear it down on unmount.
//   2. Keep that connection up. SignalR's built-in reconnect handles a
//      dropped connection (see `connection.ts`). A connect loop here
//      handles the rest: it retries a failed first `start()`, and it
//      restarts the hub after `onclose`. Both use capped backoff with
//      jitter. The loop stops only when the user must sign in.
//   3. Translate SignalR's five-state machine into our three-state UI
//      taxonomy (`connected | reconnecting | disconnected`). Every "not
//      live, but trying" case is `reconnecting`. `disconnected` means
//      "not started yet" or "stopped for sign-in".
//   4. Surface `InteractionRequiredError` from the token factory by
//      routing it through the parent shell's reauth path — same boundary
//      the REST surface uses, so the user never sees two different
//      "please sign in again" UIs.
//   5. Time the periods without a live connection and report the ones a
//      user can see (`gapTelemetry.ts`). One tracker per hub.
//
// Notes:
//   - The provider takes a `connectionFactory` for testability. Production
//     callers omit it and get `createRealtimeConnection` driven by the
//     real MSAL instance.
//   - The same `HubConnection` object serves every retry and restart, so
//     handlers that hooks bound with `on(...)` stay attached.
//   - Cleanup aborts the effect's signal. That wakes a pending backoff
//     wait at once and ends the connect loop, so no timer survives the
//     effect and no `start()` runs after it. React strict mode (mount,
//     cleanup, mount) then leaves one live loop, on the second hub.
//   - State ticks never re-render this component or its context consumers:
//     the state lives in an external store (`realtimeContext.ts`). Only
//     `useConnectionState` readers re-render; the connection context value
//     changes once, when the hub is created.
// ---------------------------------------------------------------------------

export interface RealtimeProviderProps {
    readonly instance: PublicClientApplication;
    readonly config: AppConfig;
    readonly onInteractionRequired?: () => void;
    /** Test seam — production code omits this and gets a POST to the API. */
    readonly onGapReport?: (report: GapReport) => void;
    /** Test seam — production code omits this. */
    readonly connectionFactory?: () => HubConnection;
    readonly children: ReactNode;
}

function mapHubState(state: HubConnectionState): RealtimeConnectionState {
    switch (state) {
        case HubConnectionState.Connected:
            return "connected";
        case HubConnectionState.Connecting:
        case HubConnectionState.Reconnecting:
            return "reconnecting";
        case HubConnectionState.Disconnected:
        case HubConnectionState.Disconnecting:
            return "disconnected";
    }
}

// Resolves after `ms`, or at once when `signal` aborts. Either way, its timer
// and its listener are gone when it settles.
function sleep(ms: number, signal: AbortSignal): Promise<void> {
    return new Promise((resolve) => {
        const finish = (): void => {
            clearTimeout(timer);
            signal.removeEventListener("abort", finish);
            resolve();
        };
        const timer = setTimeout(finish, ms);
        signal.addEventListener("abort", finish);
    });
}

export function RealtimeProvider({
    instance,
    config,
    onInteractionRequired,
    onGapReport,
    connectionFactory,
    children,
}: RealtimeProviderProps): ReactElement {
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const [stateStore] = useState(() => createRealtimeStateStore("disconnected"));
    const setState = stateStore.set;

    // Latest-callback ref for the reauth signal. The reauth setter is
    // owned by the parent `ExperienceShell`; capturing it via ref keeps
    // the connection-lifecycle effect stable (it re-runs only on
    // instance/config change) while still calling through to the freshest
    // setter when an interaction-required token failure surfaces.
    const interactionRequiredRef = useRef(onInteractionRequired);
    useEffect(() => {
        interactionRequiredRef.current = onInteractionRequired;
    }, [onInteractionRequired]);

    const factory = useMemo(() => {
        if (connectionFactory !== undefined) return connectionFactory;
        return () => createRealtimeConnection({ instance, config });
    }, [connectionFactory, instance, config]);

    // Where gap reports go: the API, unless a test passes its own. Read through
    // a ref, like the reauth signal above, so a new callback never restarts
    // the connection.
    const defaultGapReport = useMemo(
        () => createGapReporter({ getToken: () => acquireAccessToken({ instance, config }) }),
        [instance, config],
    );
    const gapReportRef = useRef(onGapReport ?? defaultGapReport);
    useEffect(() => {
        gapReportRef.current = onGapReport ?? defaultGapReport;
    }, [onGapReport, defaultGapReport]);

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;
        const hub = factory();
        const tracker = createGapTracker({ report: (report) => gapReportRef.current(report) });
        let hasConnected = false;

        setConnection(hub);
        setState(mapHubState(hub.state));
        // The first connect is a gap too. A boot connect under the report
        // floor is never sent.
        tracker.lost();

        // Starts the hub and retries a failed start with backoff. Attempt 0
        // starts at once. Any later attempt waits first. The loop ends when the
        // hub starts, when the effect ends, or when the user must sign in.
        const connect = async (firstAttempt: number): Promise<void> => {
            for (let attempt = firstAttempt; !signal.aborted; attempt += 1) {
                if (attempt > 0) await sleep(reconnectDelayMs(attempt), signal);
                if (signal.aborted) return;
                try {
                    await hub.start();
                } catch (err) {
                    if (signal.aborted) return;
                    if (isSignInNeeded(err)) {
                        // The reauth gate takes over. The gap stays open: the
                        // page is about to leave for sign-in.
                        setState("disconnected");
                        interactionRequiredRef.current?.();
                        return;
                    }
                    setState("reconnecting");
                    continue;
                }
                if (signal.aborted) return;
                setState("connected");
                tracker.recovered(hasConnected ? "restart" : "start");
                hasConnected = true;
                return;
            }
        };

        const handleReconnecting = (): void => {
            if (signal.aborted) return;
            setState("reconnecting");
            tracker.lost();
        };
        const handleReconnected = (): void => {
            if (signal.aborted) return;
            setState("connected");
            tracker.recovered("reconnect");
        };
        // SignalR gave up on this connection: the server closed it for good, or
        // the retry policy ended for sign-in. Start it again. The first wait is
        // 1 to 2 s, so a server that closes at once cannot cause a tight loop.
        const handleClose = (): void => {
            if (signal.aborted) return;
            setState("reconnecting");
            tracker.lost();
            void connect(1);
        };

        hub.onreconnecting(handleReconnecting);
        hub.onreconnected(handleReconnected);
        hub.onclose(handleClose);

        void connect(0);

        return () => {
            controller.abort();
            tracker.dispose();
            void hub.stop();
        };
    }, [factory, setState]);

    return (
        <RealtimeConnectionContext.Provider value={connection}>
            <RealtimeStateStoreContext.Provider value={stateStore}>
                {children}
            </RealtimeStateStoreContext.Provider>
        </RealtimeConnectionContext.Provider>
    );
}
